import { Box } from '@wix/design-system'
import { ChoiceQuestionContentComponent, useContent, useNext } from '@wix/funnel-builder-lizard'
import { Button, TextButton } from '@wix/wildcat-ui'
import { ArrowRight, ChevronLeft } from '@wix/wix-ui-icons-common'
import React, { FC, useCallback, useEffect, useState } from 'react'

import { usePageLogger } from '../../../bi/mfi'
import { createMetadataFromBusinessTerm } from '../../../category/categoryInMetadata'
import { useStorageContext } from '../../../context/StorageContext/StorageContext'
import { DesignSystemTokensLoader } from '../../reusable/DesignSystemTokensLoader'
import WixLogo from '../../reusable/icons/WixLogo.svg'
import { RadioOption } from '../../reusable/RadioOption'
import { businessTermMap } from './businessTermMap'
import { dataHooks } from './EasierCategory.dataHooks'
import s from './EasierCategory.scss'

type OptionContent = {
  text: string
}

type Content = {
  title: string
  skipButton: string
  nextButton: string
  video: string
  question: ChoiceQuestionContentComponent<{}, OptionContent>
}

export const EasierCategory: FC = () => {
  const [selectedOptionKey, setSelectedOptionKey] = useState<string | null>(null)
  const [clickedButton, setClickedButton] = useState<string>()

  const { content } = useContent<Content>()
  const { title, skipButton, nextButton, video: contentVideo, question } = content
  const { questionKey, options: allOptions } = question
  const options = allOptions.filter(o => o.optionKey in businessTermMap)

  const next = useNext()
  const pageLogger = usePageLogger()
  const { save } = useStorageContext()

  useEffect(() => {
    void pageLogger.screenView()
  }, [pageLogger])

  const handleOptionClick = useCallback(
    (optionKey: string) => {
      if (optionKey === selectedOptionKey) {
        return
      }
      void pageLogger.clickAnswer({ questionKey, buttonName: optionKey })
      setSelectedOptionKey(optionKey)
    },
    [selectedOptionKey, pageLogger, questionKey],
  )

  const handleNextClick = useCallback(() => {
    if (clickedButton || !selectedOptionKey) {
      return
    }
    setClickedButton('primary')

    const selectedIndex = options.findIndex(o => o.optionKey === selectedOptionKey)
    const entry = businessTermMap[selectedOptionKey]
    if (!entry) {
      console.error(`No business term mapping found for option key: ${selectedOptionKey}`)
      return
    }

    const { businessTerm, businessSegmentId, appDefId } = entry
    const metadata = createMetadataFromBusinessTerm(businessTerm, businessSegmentId)

    save({
      category: {
        search: '',
        searchSource: 'select',
        businessTerm,
        businessSegmentId,
        index: selectedIndex,
      },
      appDefIds: appDefId ? [appDefId] : [],
    })

    void pageLogger.answer({ answer: businessTerm, position: selectedIndex + 1 })

    const answerValues = businessTerm.displayName ? [businessTerm.displayName] : []
    next({ [questionKey]: { values: answerValues } }, metadata)
  }, [clickedButton, selectedOptionKey, options, pageLogger, questionKey, next, save])

  const handleSkipClick = useCallback(() => {
    if (clickedButton) {
      return
    }
    setClickedButton('skip')

    void pageLogger.generalAction({ action: 'skip' })

    next({ [questionKey]: { skipped: true } })
  }, [clickedButton, pageLogger, next, questionKey])

  const handleBackClick = useCallback(() => {
    if (clickedButton) {
      return
    }
    setClickedButton('back')
    void pageLogger.generalAction({ action: 'back' })
    window.history.back()
  }, [clickedButton, pageLogger])

  return (
    <DesignSystemTokensLoader type="odeditor">
      <div className={s.root} data-hook={dataHooks.template()}>
        <header className={s.header}>
          <button className={s.backBtn} onClick={handleBackClick}>
            <ChevronLeft />
          </button>
          <hr className={s.divider} />
          <WixLogo width={46} height={18} />
        </header>

        <div className={s.content}>
          <Box display="flex" align="center" paddingBottom="42px" paddingTop="78px">
            <div className={s.card}>
              <h1 className={s.title} data-hook={dataHooks.title()}>
                {title}
              </h1>

              <div className={s.options}>
                {options.map((option, index) => (
                  <RadioOption
                    key={option.optionKey}
                    name={questionKey}
                    value={option.optionKey}
                    checked={option.optionKey === selectedOptionKey}
                    onChange={() => handleOptionClick(option.optionKey)}
                    label={option.text}
                    dataHook={dataHooks.radio({ index })}
                    className={s.radioOption}
                  />
                ))}
              </div>

              <Button
                className={s.nextButton}
                size="medium"
                onClick={handleNextClick}
                disabled={!selectedOptionKey}
                isLoading={clickedButton === 'primary'}
                dataHook={dataHooks.nextButton()}
              >
                {nextButton}
              </Button>
            </div>
          </Box>
          <div className={s.right}>
            <div className={s.videoContainer}>
              {contentVideo && <video className={s.video} autoPlay muted src={contentVideo} />}
            </div>
          </div>
        </div>

        <div className={s.skip}>
          <TextButton onClick={handleSkipClick} suffixIcon={<ArrowRight />}>
            {skipButton}
          </TextButton>
        </div>
      </div>
    </DesignSystemTokensLoader>
  )
}
